import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { map, take } from 'rxjs/operators';
import { ErrorHandlerService } from './error-handler.service';
import { CaseNote } from '../models/ui/case-note.model';
import { DocNotesSdk } from '../sdks/doc-notes.sdk';
import { ExportSdk } from '../sdks/export.sdk';
import { ImportSdk } from '../sdks/import.sdk';
import { cloneDeep } from 'lodash-es';

@Injectable({
	providedIn: 'root'
})
export class CaseNoteService {
	public caseNotes$: Observable<CaseNote[]>;
	public currentCaseNote$: Observable<CaseNote>;
	public auditDocNote$: Observable<CaseNote[]>;
	private caseNotesSource: BehaviorSubject<CaseNote[]> = new BehaviorSubject([]);
	private currentCaseNoteIdSource: BehaviorSubject<string> = new BehaviorSubject<string>(null);
	private auditDocNoteSource: BehaviorSubject<CaseNote[]> = new BehaviorSubject<CaseNote[]>([]);

	constructor(
		private http: HttpClient,
		private errorHandler: ErrorHandlerService,
		private appConfigService: AppConfigService,
		private docNotesSdk: DocNotesSdk,
		private exportSdk: ExportSdk,
		private importSdk: ImportSdk
	) {
		this.caseNotes$ = this.caseNotesSource.asObservable();
		this.currentCaseNote$ = this.currentCaseNoteIdSource.pipe(
			switchMap((id) => {
				this.getDocNoteHistory(id);
				return this.caseNotes$.pipe(
					map((notes) => notes.find((note) => note.id === id))
				);
			})
		);
		this.auditDocNote$ = this.auditDocNoteSource.asObservable();
	}

	public refreshCaseNotes(): void {
		this.docNotesSdk.get({ take: 200 }).subscribe((response) => {
			this.caseNotesSource.next(response);
		});
	}

	public filterCaseNotes(searchTerm: string): void {
		this.docNotesSdk.get({ searchTerm: searchTerm, take: 200 }).subscribe((response) => {
			this.caseNotesSource.next(response);
		});
	}

	public getDocNoteHistory(id: string): void {
		this.docNotesSdk.get({ id: id }).subscribe((response) => this.auditDocNoteSource.next(response));
	}

	public setCurrentCaseNote(id: string): void {
		this.currentCaseNoteIdSource.next(id);
	}

	public saveCaseNote$(note: CaseNote): Observable<string> {
		return this.caseNotesSource.pipe(
			take(1),
			switchMap((notes) => {
				const targetIndex = notes.findIndex((note2) => note2.id === note.id);
				if (targetIndex == -1)
					return;
				const target = notes[targetIndex];
				target.isNew = false;
				target.id = null;
				return this.docNotesSdk.post({ note: target }).pipe(
					map((savedNote) => {
						notes[targetIndex] = savedNote;
						const clone = cloneDeep(notes);
						this.caseNotesSource.next(clone);
						return savedNote.id;
					})
				);

			})
		);
	}

	public exportCaseNotes() {
		this.exportSdk.exportDocNotes().subscribe((url) => {
			window.open(url, '_blank');
		});
	}

	public importCaseNotes(file: File): Observable<void> {
		return this.importSdk.importDocNotes({ file: file });
	}
}
